const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

class SubscriptionService {
  constructor() {
    this.cache = new Map();
    this.cacheTimeout = 5 * 60 * 1000; // 5 minutes
    this.pendingRequests = new Map(); // Track pending requests to prevent duplicates
  }

  /**
   * Clear all cached data
   */
  clearCache() {
    console.log('Clearing subscription service cache');
    this.cache.clear();
    this.pendingRequests.clear();
  }

  /**
   * Get cache key for user subscription
   * @param {string} token - JWT token
   * @returns {string} Cache key
   */
  getCacheKey(token) {
    // Use a simple hash of the token as cache key
    return `subscription_${btoa(token).slice(-10)}`;
  }

  /**
   * Get user's subscription status
   * @param {string} token - JWT token
   * @param {boolean} forceReload - Skip cache and force API call
   * @returns {Promise<Object>} Subscription information
   */
  async getUserSubscription(token, forceReload = false) {
    const cacheKey = this.getCacheKey(token);
    
    // Check if there's already a pending request for this token
    if (!forceReload && this.pendingRequests.has(cacheKey)) {
      console.log('Subscription service: Waiting for existing request');
      return await this.pendingRequests.get(cacheKey);
    }
    
    // Check cache first (unless force reload)
    if (!forceReload && this.cache.has(cacheKey)) {
      const cached = this.cache.get(cacheKey);
      const now = Date.now();
      
      if (now - cached.timestamp < this.cacheTimeout) {
        console.log('Subscription service: Returning cached subscription data');
        return cached.data;
      } else {
        // Cache expired
        this.cache.delete(cacheKey);
      }
    }

    // Create the promise for the API call
    const requestPromise = this._fetchSubscriptionFromAPI(token, forceReload, cacheKey);
    
    // Store the promise to prevent duplicate requests
    this.pendingRequests.set(cacheKey, requestPromise);
    
    try {
      const result = await requestPromise;
      return result;
    } finally {
      // Clean up the pending request
      this.pendingRequests.delete(cacheKey);
    }
  }

  /**
   * Internal method to fetch subscription from API
   * @private
   */
  async _fetchSubscriptionFromAPI(token, forceReload, cacheKey) {
    try {
      console.log(forceReload ? 'Subscription service: Force reloading subscription data' : 'Subscription service: Loading subscription data from API');
      
      const response = await fetch(`${API_BASE_URL}/subscriptions/user`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          // Add cache-busting header for force reload
          ...(forceReload && { 'Cache-Control': 'no-cache' })
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to get subscription information');
      }

      const data = await response.json();
      
      // Cache the result
      this.cache.set(cacheKey, {
        data,
        timestamp: Date.now()
      });

      return data;
    } catch (error) {
      throw new Error(`Failed to get subscription: ${error.message}`);
    }
  }

  /**
   * Create checkout session for subscription
   * @param {string} priceId - Stripe price ID
   * @param {string} token - JWT token
   * @param {string} successUrl - Success redirect URL
   * @param {string} cancelUrl - Cancel redirect URL
   * @returns {Promise<Object>} Checkout session information
   */
  async createCheckoutSession(priceId, token, successUrl, cancelUrl) {
    try {
      const response = await fetch(`${API_BASE_URL}/subscriptions/checkout`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ priceId, successUrl, cancelUrl }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create checkout session');
      }

      const result = await response.json();
      
      // Clear cache after successful checkout session creation
      this.clearCache();
      
      // Redirect to Stripe Checkout
      if (result.url) {
        window.location.href = result.url;
      }
      
      return result;
    } catch (error) {
      throw new Error(`Failed to create checkout session: ${error.message}`);
    }
  }

  /**
   * Cancel subscription
   * @param {string} subscriptionId - Subscription ID to cancel
   * @param {string} token - JWT token
   * @returns {Promise<Object>} Cancellation result
   */
  async cancelSubscription(subscriptionId, token) {
    try {
      const response = await fetch(`${API_BASE_URL}/subscriptions/${subscriptionId}/cancel`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to cancel subscription');
      }

      return await response.json();
    } catch (error) {
      throw new Error(`Failed to cancel subscription: ${error.message}`);
    }
  }

  /**
   * Create customer portal session
   * @param {string} token - JWT token
   * @returns {Promise<Object>} Portal session information
   */
  async createPortalSession(token) {
    try {
      const response = await fetch(`${API_BASE_URL}/subscriptions/portal`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create portal session');
      }

      return await response.json();
    } catch (error) {
      throw new Error(`Failed to create portal session: ${error.message}`);
    }
  }

  /**
   * Format subscription status for display
   * @param {string} status - Subscription status
   * @returns {Object} Formatted status
   */
  formatSubscriptionStatus(status) {
    const statusMap = {
      'active': {
        text: 'Activa',
        color: 'success',
        icon: 'fas fa-check-circle'
      },
      'trial': {
        text: 'Período de Prueba',
        color: 'info',
        icon: 'fas fa-clock'
      },
      'inactive': {
        text: 'Inactiva',
        color: 'warning',
        icon: 'fas fa-pause-circle'
      },
      'no_subscription': {
        text: 'Sin Suscripción',
        color: 'secondary',
        icon: 'fas fa-times-circle'
      },
      'cancelled': {
        text: 'Cancelada',
        color: 'danger',
        icon: 'fas fa-times-circle'
      },
      'past_due': {
        text: 'Pago Pendiente',
        color: 'warning',
        icon: 'fas fa-exclamation-triangle'
      },
      'unpaid': {
        text: 'Sin Pagar',
        color: 'danger',
        icon: 'fas fa-exclamation-triangle'
      },
      'trialing': {
        text: 'En Prueba',
        color: 'info',
        icon: 'fas fa-clock'
      }
    };

    return statusMap[status] || {
      text: status,
      color: 'secondary',
      icon: 'fas fa-question-circle'
    };
  }

  /**
   * Format price for display
   * @param {number} amount - Amount in cents
   * @param {string} currency - Currency code
   * @returns {string} Formatted price
   */
  formatPrice(amount, currency = 'USD') {
    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
    });

    return formatter.format(amount / 100);
  }

  /**
   * Format date for display
   * @param {number} timestamp - Unix timestamp
   * @returns {string} Formatted date
   */
  formatDate(timestamp) {
    if (!timestamp || typeof timestamp !== 'number') {
      console.warn('Invalid timestamp:', timestamp);
      return 'N/A';
    }
    
    try {
      const date = new Date(timestamp * 1000);
      if (isNaN(date.getTime())) {
        console.warn('Invalid timestamp produces invalid date:', timestamp);
        return 'N/A';
      }
      
      return date.toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      console.error('Error formatting timestamp:', error, timestamp);
      return 'N/A';
    }
  }

  /**
   * Format ISO date string for display
   * @param {string} isoDateString - ISO date string
   * @returns {string} Formatted date
   */
  formatISODate(isoDateString) {
    if (!isoDateString) return 'N/A';
    
    try {
      const date = new Date(isoDateString);
      if (isNaN(date.getTime())) {
        console.warn('Invalid date string:', isoDateString);
        return 'N/A';
      }
      return date.toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      console.error('Error formatting date:', error, isoDateString);
      return 'N/A';
    }
  }

  /**
   * Calculate days remaining until date
   * @param {string} endDate - ISO date string
   * @returns {number} Days remaining
   */
  calculateDaysRemaining(endDate) {
    if (!endDate) return 0;
    
    try {
      const end = new Date(endDate);
      if (isNaN(end.getTime())) {
        console.warn('Invalid end date:', endDate);
        return 0;
      }
      
      const now = new Date();
      const diffTime = end - now;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return Math.max(0, diffDays);
    } catch (error) {
      console.error('Error calculating days remaining:', error, endDate);
      return 0;
    }
  }

  /**
   * Redirect to Stripe Checkout
   * @param {string} sessionUrl - Checkout session URL
   */
  redirectToCheckout(sessionUrl) {
    window.location.href = sessionUrl;
  }

  /**
   * Redirect to Customer Portal
   * @param {string} portalUrl - Portal session URL
   */
  redirectToPortal(portalUrl) {
    window.location.href = portalUrl;
  }
}

const subscriptionService = new SubscriptionService();
export default subscriptionService;
